import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import BackButton from '@sorare/core/src/atoms/buttons/BackButton';
import CloseButton from '@sorare/core/src/atoms/buttons/CloseButton';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { PlayerGameScoreScore } from '@sorare/core/src/components/scoring/PlayerGameScoreScore';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { PrimaryBuyForAPlayer } from '@sorare/gameplay/src/components/primaryBuy/PrimaryBuyForAPlayer';
import { BriefGame } from '@sorare/gameplay/src/components/so5/BriefGame';
import { BriefPlayerScore } from '@sorare/gameplay/src/components/stats/BriefPlayerScore';
import MatchViewDialog from '@sorare/gameplay/src/components/stats/MatchViewDialog';
import { PlayerGameScoreStats } from '@sorare/gameplay/src/components/stats/PlayerGameScoreStats';
import { isGameStarted } from '@sorare/gameplay/src/lib/so5';

import {
  PlayerGameScoreQuery,
  PlayerGameScoreQueryVariables,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  background: var(--c-neutral-200);
  padding: var(--double-unit);
`;

const PLAYER_GAME_SCORE_QUERY = gql`
  query PlayerGameScoreQuery($id: ID!) {
    so5 {
      playerGameScore(id: $id) {
        id
        scoreStatus
        anyPlayer {
          slug
          avatarPictureUrl
          ...BriefPlayerScore_anyPlayerInterface
        }
        anyGame {
          id
          ...BriefGame_anyGameInterface
        }
        ...BriefPlayerScore_playerGameScoreInterface
        ...PlayerGameScoreScore_playerGameScoreInterface
        ...PlayerGameScoreStats_playerGameScoreInterface
      }
    }
  }
  ${BriefGame.fragments.anyGameInterface}
  ${BriefPlayerScore.fragments.anyPlayerInterface}
  ${BriefPlayerScore.fragments.playerGameScoreInterface}
  ${PlayerGameScoreScore.fragments.playerGameScoreInterface}
  ${PlayerGameScoreStats.fragments.playerGameScoreInterface}
` as TypedDocumentNode<PlayerGameScoreQuery, PlayerGameScoreQueryVariables>;

type Props = {
  scoreId: string;
  onClose: () => void;
  onBack: () => void;
  showUpsell?: boolean;
};

export const PlayerGameScore = ({
  scoreId,
  onClose,
  onBack,
  showUpsell,
}: Props) => {
  const [openMatchView, setOpenMatchView] = useState(false);
  const { data, loading } = useQuery(PLAYER_GAME_SCORE_QUERY, {
    variables: {
      id: scoreId,
    },
  });

  const { playerGameScore } = data?.so5 || {};

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  if (!playerGameScore) {
    return null;
  }

  return (
    <Root gap={3}>
      <SBHorizontal>
        <BackButton onClick={onBack} />
        <BodyM as="p" bold>
          <FormattedMessage {...fantasy.playerPerformance} />
        </BodyM>
        <CloseButton onClose={onClose} />
      </SBHorizontal>
      <BriefPlayerScore
        pictureUrl={playerGameScore.anyPlayer.avatarPictureUrl}
        anyPlayer={playerGameScore.anyPlayer}
        playerGameScores={[playerGameScore]}
      >
        <PlayerGameScoreScore playerGameScore={playerGameScore} />
      </BriefPlayerScore>
      <BriefGame
        game={playerGameScore.anyGame}
        onClick={() => setOpenMatchView(true)}
      />
      {showUpsell && (
        <PrimaryBuyForAPlayer playerSlug={playerGameScore.anyPlayer.slug} />
      )}
      {playerGameScore.scoreStatus !== ScoreStatus.DID_NOT_PLAY &&
        isGameStarted(playerGameScore.anyGame.status) && (
          <PlayerGameScoreStats playerGameScores={[playerGameScore]} />
        )}
      {!!openMatchView && (
        <MatchViewDialog
          open
          onClose={() => setOpenMatchView(false)}
          gameId={playerGameScore.anyGame.id}
        />
      )}
    </Root>
  );
};
